import { useEffect, useState } from "react";
import { Activity, CreditCard, Users } from "lucide-react";
import { api } from "../api";

const Big = ({ label, value, gold, testId }) => (
    <div className="rounded-3xl border border-lakou-gold/15 bg-lakou-coal/60 p-5 text-center" data-testid={testId}>
        <div className={`font-display font-black text-4xl ${gold ? "text-goldgrad" : "text-lakou-cream"}`}>{value}</div>
        <div className="mt-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-lakou-sand">{label}</div>
    </div>
);

export const ControlLive = () => {
    const [d, setD] = useState(null);

    useEffect(() => {
        let on = true;
        const load = () => api.get("/control/live").then((r) => { if (on) setD(r.data); }).catch(() => {});
        load();
        const t = setInterval(load, 8000);
        return () => { on = false; clearInterval(t); };
    }, []);

    if (!d) return <p className="text-lakou-sand text-sm" data-testid="live-loading">Loading…</p>;

    return (
        <div className="space-y-6" data-testid="control-live">
            <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lakou-gold opacity-60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-lakou-gold" /></span>
                <h1 className="font-display font-black text-xl">Live Event Control</h1>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Big label="Checked in" value={d.checked_in} gold testId="live-checked" />
                <Big label="Remaining" value={d.remaining} testId="live-remaining" />
                <Big label="Tickets sold" value={d.sold} testId="live-sold" />
                <Big label="Attendance" value={`${d.attendance_pct}%`} testId="live-pct" />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Big label="GA in" value={d.ga_checked_in} testId="live-ga" />
                <Big label="VIP in" value={d.vip_checked_in} testId="live-vip" />
            </div>
            <div className="h-2.5 rounded-full bg-lakou-raise overflow-hidden" data-testid="live-progress">
                <div className="h-full rounded-full bg-gradient-to-r from-lakou-orange to-lakou-gold transition-all duration-700" style={{ width: `${Math.min(d.attendance_pct, 100)}%` }} />
            </div>

            <section>
                <h2 className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-gold mb-3 flex items-center gap-2"><Activity size={12} /> Recent check-ins</h2>
                <div className="space-y-2" data-testid="live-recent-checkins">
                    {d.recent_checkins.map((t) => (
                        <div key={t.code} className="flex items-center justify-between rounded-xl border border-lakou-gold/10 bg-lakou-coal/50 px-4 py-2.5">
                            <div className="min-w-0">
                                <div className="text-sm text-lakou-cream truncate">{t.attendee_name}</div>
                                <div className="font-mono2 text-[9px] text-lakou-sand">{t.code} · {t.category}</div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="font-mono2 text-[9px] text-lakou-gold">{t.used_at?.slice(11, 19)}</div>
                                <div className="font-mono2 text-[8px] text-lakou-sand">{t.checked_in_by || ""}</div>
                            </div>
                        </div>
                    ))}
                    {!d.recent_checkins.length && <p className="text-xs text-lakou-sand">No check-ins yet.</p>}
                </div>
            </section>

            <section>
                <h2 className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-gold mb-3 flex items-center gap-2"><CreditCard size={12} /> Recent payments</h2>
                <div className="space-y-2" data-testid="live-recent-payments">
                    {d.recent_payments.map((o) => (
                        <div key={o.id} className="flex items-center justify-between rounded-xl border border-lakou-gold/10 bg-lakou-coal/50 px-4 py-2.5">
                            <div className="text-sm text-lakou-cream truncate">{o.name} <span className="text-lakou-sand">· {o.category_name}</span></div>
                            <div className="font-mono2 text-[10px] text-lakou-gold shrink-0">${o.amount?.toFixed(2)} {(o.payment_method || "").toUpperCase()}</div>
                        </div>
                    ))}
                    {!d.recent_payments.length && <p className="text-xs text-lakou-sand">No payments yet.</p>}
                </div>
            </section>

            <section>
                <h2 className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-gold mb-3 flex items-center gap-2"><Users size={12} /> Scanner activity</h2>
                <div className="space-y-2" data-testid="live-scanners">
                    {d.scanners.map((s) => (
                        <div key={s.email} className="flex items-center justify-between rounded-xl border border-lakou-gold/10 bg-lakou-coal/50 px-4 py-2.5">
                            <span className="text-sm text-lakou-cream">{s.email}</span>
                            <span className="font-mono2 text-[10px] text-lakou-gold">{s.count} scans</span>
                        </div>
                    ))}
                    {!d.scanners.length && <p className="text-xs text-lakou-sand">No scanner activity yet.</p>}
                </div>
            </section>
        </div>
    );
};
